import { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Input } from '@opendoor/atoms/input';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import * as Sentry from '@sentry/nextjs';
import type {
  AddressSearchResultFragment,
  OdProtosGeoDataStoreData_Zone_Type,
} from '__generated__/athena';
import { getAthenaClient } from 'components/api';
import { debounce } from 'es-toolkit';
import { formatCurrency } from 'helpers/formatters';

import { useOutsideCaller } from '../helpers/useOutsideCaller';
import type { MapLocation } from './types';

const formatZoneType = (zoneType?: OdProtosGeoDataStoreData_Zone_Type): string => {
  switch (zoneType) {
    case 'CITY':
      return '(City)';
    case 'COUNTY':
      return '(County)';
    case 'ZIP_CODE':
      return '(Zip code)';
    case 'NEIGHBORHOOD':
    case 'SUBNEIGHBORHOOD':
    case 'MACRO_NEIGHBORHOOD':
      return '(Neighborhood)';
    default:
      return '';
  }
};

export interface LocationSearchProps {
  isFocused: boolean;
  selectedLocation?: MapLocation;
  setSelectedLocation: (location?: MapLocation | null) => void;
  selectedAddress?: AddressSearchResultFragment;
  setSelectedAddress: (address?: AddressSearchResultFragment | null) => void;
  /** Input height. `sm` (56px) on the desktop quick-filters bar; `xs` (36px) in
   * the compact mobile header. */
  size?: 'xs' | 'sm' | 'base' | 'lg';
}

const QUERY_STRING_MIN_LENGTH = 2;
const SEARCH_DEBOUNCE_MS = 400;

export const MapLocationSearch = ({
  isFocused,
  selectedAddress,
  setSelectedAddress,
  selectedLocation,
  setSelectedLocation,
  size = 'sm',
}: LocationSearchProps) => {
  const [locationOptions, setLocationOptions] = useState<Array<MapLocation> | undefined>(
    selectedLocation ? [selectedLocation] : undefined,
  );
  const [addressOptions, setAddressOptions] = useState<
    Array<AddressSearchResultFragment> | undefined
  >();
  const [query, setQuery] = useState<string | undefined>();
  const [isLocationFocused, setIsLocationFocused] = useState(false);
  const [isDropdownFocused, setIsDropdownFocused] = useState(false);
  const [isFetchingSuggestions, setIsFetchingSuggestions] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const containerRef = useRef<HTMLElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const debouncedSearchLocations = useMemo(() => {
    const fetchLocations = async (queryStr?: string) => {
      abortControllerRef.current?.abort();

      if (queryStr && queryStr.length > QUERY_STRING_MIN_LENGTH) {
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setIsFetchingSuggestions(true);
        setFetchError(false);
        try {
          const types: OdProtosGeoDataStoreData_Zone_Type[] = queryStr.match(/^\d/)
            ? ['ZIP_CODE']
            : ['CITY', 'NEIGHBORHOOD', 'SUBNEIGHBORHOOD'];
          const client = getAthenaClient(undefined, { signal: controller.signal });
          const [locationsResp, addressResp] = await Promise.all([
            client.SearchLocations({ query: queryStr, types }),
            client.AddressSearch({ query: queryStr }),
          ]);
          const zones =
            locationsResp?.geoDataStore?.searchZonesByTextQuery?.zones
              ?.map((result) => {
                const { zone } = result;
                return {
                  label: zone?.name + ', ' + zone?.stateAbbr,
                  zoneType: formatZoneType(zone?.zoneType),
                  value: zone?.uuid,
                  coordinates: zone?.centroid ? JSON.parse(zone?.centroid)?.coordinates : [0, 0],
                  population: zone?.population,
                } as MapLocation;
              })
              .toSorted((a, b) => {
                if (a.population && b.population) {
                  return b.population - a.population;
                }
                return 0;
              }) ?? [];
          setLocationOptions(zones);
          setAddressOptions(addressResp?.marketplace?.addressSearch?.properties ?? []);
        } catch (error) {
          if (error instanceof DOMException && error.name === 'AbortError') {
            return;
          }
          Sentry.captureException(error);
          setFetchError(true);
          setLocationOptions(undefined);
          setAddressOptions(undefined);
        } finally {
          if (!controller.signal.aborted) {
            setIsFetchingSuggestions(false);
          }
        }
      } else {
        abortControllerRef.current = null;
        setLocationOptions(undefined);
        setAddressOptions(undefined);
        setIsFetchingSuggestions(false);
        setFetchError(false);
      }
    };
    return debounce(fetchLocations, SEARCH_DEBOUNCE_MS);
  }, []);

  useEffect(() => {
    debouncedSearchLocations(query);
    return () => {
      debouncedSearchLocations.cancel();
      abortControllerRef.current?.abort();
    };
  }, [query, debouncedSearchLocations]);

  useOutsideCaller(() => {
    setIsLocationFocused(false);
    setIsDropdownFocused(false);
  }, [containerRef]);

  const handleLocationSearch = (search: string) => {
    setQuery(search);
  };

  const handleLocationFocus = () => {
    setIsLocationFocused(true);
    setQuery('');
    setSelectedLocation(undefined);
    setSelectedAddress(undefined);
  };
  const handleDropdownFocus = () => setIsDropdownFocused(true);
  const handleDropdownBlur = () => {
    setIsLocationFocused(false);
    setIsDropdownFocused(false);
  };

  const handleLocationSelect = (location: MapLocation) => {
    setSelectedLocation(location);
    setSelectedAddress(undefined);
    handleDropdownBlur();
    handleLocationSearch('');
  };

  const handleAddressSelect = (address: AddressSearchResultFragment) => {
    setSelectedAddress(address);
    setSelectedLocation(undefined);
    handleDropdownBlur();
    handleLocationSearch('');
  };

  const hasSuggestionResults =
    (locationOptions?.length ?? 0) > 0 || (addressOptions?.length ?? 0) > 0;

  const showNoResultsMessage = useMemo(() => {
    if (!query || query.length <= QUERY_STRING_MIN_LENGTH) {
      return false;
    }
    if (isFetchingSuggestions || fetchError) {
      return false;
    }
    return !hasSuggestionResults;
  }, [query, isFetchingSuggestions, fetchError, hasSuggestionResults]);

  const shouldShowDropdown = useMemo(
    () =>
      !!query &&
      (isLocationFocused || isDropdownFocused) &&
      (hasSuggestionResults || showNoResultsMessage || isFetchingSuggestions || fetchError),
    [
      query,
      hasSuggestionResults,
      showNoResultsMessage,
      isFetchingSuggestions,
      fetchError,
      isLocationFocused,
      isDropdownFocused,
    ],
  );

  return (
    <Col className="w-full" ref={containerRef}>
      <Text as="label" className="text-label-md pb-3 text-od-content-primary max-md:hidden">
        Location
      </Text>
      <Input
        aria-label="Location search"
        id="locations-search"
        name="locations"
        size={size}
        placeholder="City, neighborhood, street address, or ZIP"
        onChange={(e) => handleLocationSearch(e.target.value)}
        onFocus={handleLocationFocus}
        defaultValue={selectedAddress?.fullAddress || selectedLocation?.label}
        className={cn('w-full max-md:pr-10', isFocused && 'max-md:pl-10')}
        key={selectedAddress?.fullAddress || selectedLocation?.label}
      />
      <Box id="locations" className="relative w-full" key={`location-results-${query}`}>
        <Col
          className={cn(
            'absolute left-0 top-2 z-[10000] max-h-100 min-h-30 w-full overflow-x-hidden overflow-y-scroll rounded-2xl border border-od-border-accent-brand bg-od-bg-primary',
            shouldShowDropdown ? 'flex' : 'hidden',
          )}
          onFocus={handleDropdownFocus}
          onBlur={handleDropdownBlur}
        >
          {isFetchingSuggestions ? (
            <Box className="w-full px-5 py-5">
              <Text as="p" className="text-body-sm text-od-content-secondary">
                Searching…
              </Text>
            </Box>
          ) : null}
          {fetchError ? (
            <Box className="w-full px-5 py-5" role="alert">
              <Text as="p" className="text-body-sm text-od-content-primary">
                Something went wrong. Please try again.
              </Text>
            </Box>
          ) : null}
          {showNoResultsMessage ? (
            <Col className="w-full gap-2 px-5 py-5" role="status" aria-live="polite">
              <Text as="p" className="text-body-sm text-od-content-primary">
                No matches yet
              </Text>
              <Text as="p" className="text-label-sm text-od-content-secondary">
                Try a street number and name, ZIP code, or city. You can also pan the map to search
                this area.
              </Text>
            </Col>
          ) : null}
          {query && locationOptions?.length && locationOptions.length > 0 ? (
            <Col className="w-full bg-od-bg-primary">
              <Text as="p" className="text-label-sm pl-5 pt-2">
                Locations
              </Text>
              {locationOptions?.map((item, index) => (
                <Col
                  key={item.label}
                  className="items-start border-b border-od-border-secondary hover:bg-od-bg-secondary"
                >
                  <Box
                    aria-label={`Select ${item.label}`}
                    analyticsName="comos-map-select-location"
                    onClick={() => handleLocationSelect(item)}
                    className={cn(
                      'relative w-full flex-row px-5 py-5 active:bg-od-bg-secondary',
                      index === 0 && 'pt-2.5',
                    )}
                  >
                    <Text as="span" className="text-body-sm">
                      {item.label} {item.zoneType}
                    </Text>
                  </Box>
                </Col>
              ))}
            </Col>
          ) : null}
          {query && addressOptions && addressOptions?.length > 0 ? (
            <Col className="w-full bg-od-bg-primary">
              <Text as="p" className="text-label-sm pl-5 pt-6">
                Listings
              </Text>
              {addressOptions?.map((item, index) => (
                <Col
                  key={item.addressToken || item.fullAddress || String(index)}
                  className="items-start border-b border-od-border-secondary hover:bg-od-bg-secondary"
                >
                  <Box
                    aria-label={`Select ${item.fullAddress}`}
                    analyticsName="cosmos-map-select-address"
                    onClick={() => handleAddressSelect(item)}
                    className={cn(
                      'relative w-full flex-row px-5 py-5 active:bg-od-bg-secondary',
                      index === 0 && 'pt-2.5',
                    )}
                  >
                    <Col className="items-start">
                      <Text as="p" className="text-body-sm w-full">
                        {item.fullAddress}
                      </Text>
                      {item.priceCents && (
                        <Text as="p" className="text-label-sm self-start">
                          {formatCurrency(item.priceCents)}
                        </Text>
                      )}
                    </Col>
                  </Box>
                </Col>
              ))}
            </Col>
          ) : null}
        </Col>
      </Box>
    </Col>
  );
};
